import { Component, OnInit, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { TooltipModule } from 'primeng/tooltip';
import { TextareaModule } from 'primeng/textarea';
import { ToastModule } from 'primeng/toast';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { ConfirmationService, MessageService } from 'primeng/api';
import { FormsModule } from '@angular/forms';
import { Observable, catchError, forkJoin, of } from 'rxjs';
import { PropertyAdminService } from '../../../core/services/property-admin.service';
import { AuthService } from '../../../core/services/auth.service';
import { PropertyDocumentResponse, PropertyResponse } from '../../../core/models';
import { AppEmptyState } from '../../../shared/ui/empty-state/app-empty-state';
import { AppStatusTag, StatusTagSeverity } from '../../../shared/ui/status-tag/app-status-tag';
import { AppDialog } from '../../../shared/ui/dialog/app-dialog';
import { PropertyEditDialogComponent } from '../property-edit-dialog/property-edit-dialog';
import { propertyStatusLabel, propertyStatusSeverity } from '../property-labels';

interface StatusAction { target: string; label: string; icon: string; }

// Miroir de Property.changeStatusByAdmin (property-api) : transitions manuelles autorisées.
const STATUS_ACTIONS: Record<string, StatusAction[]> = {
  PUBLISHED: [
    { target: 'TAKEN',          label: 'Marquer pris',      icon: 'pi pi-flag' },
    { target: 'PENDING_REVIEW', label: 'Remettre en revue', icon: 'pi pi-replay' }
  ],
  TAKEN:    [{ target: 'PUBLISHED',      label: 'Republier',         icon: 'pi pi-send' }],
  REJECTED: [{ target: 'PENDING_REVIEW', label: 'Remettre en revue', icon: 'pi pi-replay' }]
};

@Component({
  selector: 'app-property-detail',
  standalone: true,
  imports: [CommonModule, RouterLink, ButtonModule, ProgressSpinnerModule,
    TooltipModule, TextareaModule, ToastModule, FormsModule, ConfirmDialogModule,
    AppEmptyState, AppStatusTag, AppDialog, PropertyEditDialogComponent],
  templateUrl: './property-detail.html',
  styleUrl: './property-detail.scss'
})
export class PropertyDetailComponent implements OnInit {
  private svc   = inject(PropertyAdminService);
  private route = inject(ActivatedRoute);
  private toast = inject(MessageService);
  private confirm = inject(ConfirmationService);
  private router  = inject(Router);
  authService   = inject(AuthService);

  propertyId    = '';
  loading       = signal(true);
  property      = signal<PropertyResponse | null>(null);
  documents     = signal<PropertyDocumentResponse[]>([]);
  photoUrls     = signal<string[]>([]);
  loadingPhotos = signal(false);
  rejectId      = signal<string | null>(null);
  rejectText    = '';
  processing    = signal<string | null>(null);

  // Actions sur le bien lui-même
  editVisible         = signal(false);
  rejectPropVisible   = signal(false);
  rejectPropText      = '';
  propertyAction      = signal<string | null>(null);

  ngOnInit() {
    this.propertyId = this.route.snapshot.paramMap.get('id')!;

    forkJoin({
      property: this.svc.getProperty(this.propertyId).pipe(catchError(() => of(null))),
      docs:     this.svc.getPropertyDocuments(this.propertyId).pipe(catchError(() => of([])))
    }).subscribe(({ property, docs }) => {
      this.property.set(property);
      this.documents.set(docs);
      this.loading.set(false);

      const photoIds = property?.photoDocumentIds ?? [];
      if (photoIds.length) {
        this.loadingPhotos.set(true);
        forkJoin(
          photoIds.map(id => this.svc.getPhotoUrl(id).pipe(catchError(() => of(''))))
        ).subscribe(urls => {
          this.photoUrls.set(urls.filter(u => !!u));
          this.loadingPhotos.set(false);
        });
      }
    });
  }

  countByStatus(status: string): number {
    return this.documents().filter(d => d.status === status).length;
  }

  openDoc(docId: string) {
    this.svc.getDocumentUrl(docId).pipe(catchError(() => of(''))).subscribe(url => {
      if (url) window.open(url, '_blank');
    });
  }

  validate(doc: PropertyDocumentResponse) {
    this.processing.set(doc.id);
    this.svc.validateDocument(doc.id).pipe(catchError(() => of(null))).subscribe(res => {
      if (res) {
        this.updateDoc(doc.id, 'VALIDATED');
        this.toast.add({ severity: 'success', summary: 'Document validé', life: 3500 });
      } else {
        this.toast.add({ severity: 'error', summary: 'Erreur', detail: 'La validation a échoué.', life: 5000 });
      }
      this.processing.set(null);
    });
  }

  startReject(docId: string) {
    this.rejectId.set(docId);
    this.rejectText = '';
  }

  confirmReject(doc: PropertyDocumentResponse) {
    if (!this.rejectText.trim()) return;
    this.processing.set(doc.id);
    this.svc.rejectDocument(doc.id, this.rejectText).pipe(catchError(() => of(null))).subscribe(res => {
      if (res) {
        this.updateDoc(doc.id, 'REJECTED');
        this.toast.add({ severity: 'warn', summary: 'Document rejeté', life: 3500 });
      } else {
        this.toast.add({ severity: 'error', summary: 'Erreur', detail: 'Le rejet a échoué.', life: 5000 });
      }
      this.processing.set(null);
      this.rejectId.set(null);
    });
  }

  openPhotoInTab(url: string) {
    window.open(url, '_blank');
  }

  // ── Actions sur le bien ────────────────────────────────────────────────────────

  statusActions(): StatusAction[] {
    return STATUS_ACTIONS[this.property()?.status ?? ''] ?? [];
  }

  onEdited(updated: PropertyResponse) {
    this.property.update(p => p ? { ...p, ...updated } : updated);
  }

  approveProperty() {
    this.runPropertyAction('approve', this.svc.approveProperty(this.propertyId), 'Bien approuvé et publié');
  }

  openRejectProperty() {
    this.rejectPropText = '';
    this.rejectPropVisible.set(true);
  }

  confirmRejectProperty() {
    const reason = this.rejectPropText.trim();
    if (!reason) return;
    this.runPropertyAction('reject', this.svc.rejectProperty(this.propertyId, reason), 'Bien rejeté',
      () => this.rejectPropVisible.set(false));
  }

  changeStatus(action: StatusAction) {
    this.runPropertyAction(action.target, this.svc.changeStatus(this.propertyId, action.target),
      `Statut : ${propertyStatusLabel(action.target)}`);
  }

  confirmDelete(event: Event) {
    this.confirm.confirm({
      target: event.target as EventTarget,
      header: 'Supprimer ce bien ?',
      message: `« ${this.property()?.title} » sera retiré de toutes les listes et de la recherche. `
        + 'Il reste restaurable depuis l\'onglet Corbeille.',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Supprimer',
      rejectLabel: 'Annuler',
      acceptButtonProps: { severity: 'danger' },
      rejectButtonProps: { severity: 'secondary', outlined: true },
      accept: () => {
        this.propertyAction.set('delete');
        this.svc.deleteProperty(this.propertyId).subscribe({
          next: () => {
            this.toast.add({ severity: 'success', summary: 'Bien supprimé', detail: 'Restaurable depuis la corbeille.', life: 4000 });
            this.router.navigate(['/properties']);
          },
          error: () => {
            this.propertyAction.set(null);
            this.toast.add({ severity: 'error', summary: 'Erreur', detail: 'La suppression a échoué.', life: 5000 });
          }
        });
      }
    });
  }

  private runPropertyAction(key: string, call: Observable<PropertyResponse>, success: string, done?: () => void) {
    this.propertyAction.set(key);
    call.subscribe({
      next: updated => {
        this.onEdited(updated);
        this.propertyAction.set(null);
        this.toast.add({ severity: 'success', summary: success, life: 3500 });
        done?.();
      },
      error: err => {
        this.propertyAction.set(null);
        this.toast.add({ severity: 'error', summary: 'Erreur', detail: err?.error?.message ?? "L'action a échoué.", life: 5000 });
      }
    });
  }

  propertyStatusLabel    = propertyStatusLabel;
  propertyStatusSeverity = propertyStatusSeverity;

  private updateDoc(id: string, status: string) {
    this.documents.update(docs => docs.map(d => d.id === id ? { ...d, status } : d));
  }

  statusLabel(s: string): string {
    const map: Record<string, string> = { VALIDATED: 'Validé', REJECTED: 'Rejeté', PENDING: 'En attente' };
    return map[s] ?? s;
  }

  statusSeverity(s: string): StatusTagSeverity {
    const map: Record<string, StatusTagSeverity> = {
      VALIDATED: 'success', REJECTED: 'danger', PENDING: 'warn'
    };
    return map[s] ?? 'secondary';
  }

  docTypeLabel(t: string): string {
    const map: Record<string, string> = {
      IDENTITY:        "Pièce d'identité",
      TITLE_DEED:      'Titre de propriété',
      TITRE_PROPRIETE: 'Titre de propriété',
      TITRE_FONCIER:   'Titre foncier',
      PLAN_MASSE:      'Plan de masse',
      PLAN_BORNAGE:    'Plan de bornage',
      PERMIS_CONSTRUIRE:     'Permis de construire',
      CERTIFICAT_CONFORMITE: 'Certificat de conformité',
      BAIL_COMMERCIAL: 'Bail commercial',
      LAND_SURVEY:     'Certificat de bornage',
      BUILDING_PERMIT: 'Permis de construire',
      TAX_RECEIPT:     'Quittance fiscale',
    };
    return map[t] ?? t?.replace(/_/g, ' ') ?? 'Document';
  }

  docIcon(t: string): string {
    const map: Record<string, string> = {
      IDENTITY: 'pi-id-card', TITLE_DEED: 'pi-verified', TITRE_PROPRIETE: 'pi-verified',
      TITRE_FONCIER: 'pi-file', PLAN_MASSE: 'pi-map', PLAN_BORNAGE: 'pi-map-marker',
      PERMIS_CONSTRUIRE: 'pi-building', CERTIFICAT_CONFORMITE: 'pi-verified',
      BAIL_COMMERCIAL: 'pi-file-edit', LAND_SURVEY: 'pi-map',
      BUILDING_PERMIT: 'pi-building', TAX_RECEIPT: 'pi-receipt',
    };
    return map[t] ?? 'pi-file';
  }

  typeLabel(t: string): string {
    const map: Record<string, string> = {
      APPARTEMENT: 'Appartement', MAISON: 'Maison', TERRAIN: 'Terrain',
      VILLA: 'Villa', BUREAU: 'Bureau', LOCAL_COMMERCIAL: 'Local commercial',
      ENTREPOT: 'Entrepôt', HOTEL: 'Hôtel'
    };
    return map[t] ?? t;
  }
}
